import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import { Button, Input, Alert } from '../../components/ui'
import { register, forgotPassword, resetPassword, twoFactorChallenge } from '../../lib/api'

// ── Register ─────────────────────────────────────────────────────────────────
export function RegisterPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const { success } = useToast()
  const [form, setForm] = useState({ name: '', email: '', password: '', password_confirmation: '', company_name: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }))

  const handle = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await register(form)
      success('Account created!')
      navigate(data.redirect || '/engage')
    } catch (err) {
      const errs = err.response?.data?.errors
      setError(errs ? Object.values(errs)[0][0] : err.response?.data?.message || 'Registration failed.')
    } finally { setLoading(false) }
  }

  return (
    <>
      <h2 className="font-display font-bold text-xl text-white mb-1">Create your account</h2>
      <p className="text-sm text-[var(--text-dim)] mb-7">Start your Systeza journey</p>
      {error && <Alert type="error" className="mb-5">{error}</Alert>}
      <form onSubmit={handle} className="space-y-4">
        <Input label="Full name" value={form.name} onChange={f('name')} placeholder="Jane Smith" required />
        <Input label="Email address" type="email" value={form.email} onChange={f('email')} placeholder="you@company.com" required />
        <Input label="Company (optional)" value={form.company_name} onChange={f('company_name')} placeholder="ACME Corp" />
        <Input label="Password" type="password" value={form.password} onChange={f('password')} placeholder="Min 8 chars" required />
        <Input label="Confirm password" type="password" value={form.password_confirmation} onChange={f('password_confirmation')} placeholder="Repeat password" required />
        <Button type="submit" loading={loading} className="w-full justify-center py-3 mt-1">Create account</Button>
      </form>
      <p className="text-center text-sm text-[var(--text-dim)] mt-6">
        Already have an account?{' '}<Link to="/login" className="text-[var(--color-accent)] hover:underline font-medium">Sign in</Link>
      </p>
    </>
  )
}

// ── Forgot Password ───────────────────────────────────────────────────────────
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const handle = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await forgotPassword(email)
      setSent(true)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset link.')
    } finally { setLoading(false) }
  }

  if (sent) return (
    <>
      <div className="text-center py-6">
        <div className="text-4xl mb-4">📧</div>
        <h2 className="font-display font-bold text-lg text-white mb-2">Check your inbox</h2>
        <p className="text-sm text-[var(--text-dim)] mb-6">We've sent a password reset link to <strong className="text-white">{email}</strong></p>
        <Link to="/login" className="text-[var(--color-accent)] text-sm hover:underline">← Back to login</Link>
      </div>
    </>
  )

  return (
    <>
      <h2 className="font-display font-bold text-xl text-white mb-1">Reset your password</h2>
      <p className="text-sm text-[var(--text-dim)] mb-7">Enter your email and we'll send a reset link.</p>
      {error && <Alert type="error" className="mb-5">{error}</Alert>}
      <form onSubmit={handle} className="space-y-5">
        <Input label="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
        <Button type="submit" loading={loading} className="w-full justify-center py-3">Send reset link</Button>
      </form>
      <p className="text-center mt-5"><Link to="/login" className="text-sm text-[var(--color-accent)] hover:underline">← Back to login</Link></p>
    </>
  )
}

// ── Reset Password ────────────────────────────────────────────────────────────
export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { success } = useToast()
  const [form, setForm] = useState({ password: '', password_confirmation: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }))

  const handle = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await resetPassword({ token: params.get('token'), email: params.get('email'), ...form })
      success('Password reset! Please sign in.')
      navigate('/login')
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed.')
    } finally { setLoading(false) }
  }

  return (
    <>
      <h2 className="font-display font-bold text-xl text-white mb-1">Set new password</h2>
      <p className="text-sm text-[var(--text-dim)] mb-7">Choose a strong new password.</p>
      {error && <Alert type="error" className="mb-5">{error}</Alert>}
      <form onSubmit={handle} className="space-y-5">
        <Input label="New password" type="password" value={form.password} onChange={f('password')} placeholder="Min 8 chars" required />
        <Input label="Confirm password" type="password" value={form.password_confirmation} onChange={f('password_confirmation')} placeholder="Repeat password" required />
        <Button type="submit" loading={loading} className="w-full justify-center py-3">Reset password</Button>
      </form>
    </>
  )
}

// ── 2FA Challenge ─────────────────────────────────────────────────────────────
export function TwoFactorPage() {
  const [params] = useSearchParams()
  const { refreshUser } = useAuth()
  const navigate = useNavigate()
  const { success } = useToast()
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handle = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const data = await twoFactorChallenge(code, params.get('token'))
      await refreshUser()
      success('Authenticated!')
      navigate(data.redirect || '/engage')
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code.')
    } finally { setLoading(false) }
  }

  return (
    <>
      <h2 className="font-display font-bold text-xl text-white mb-1">Two-factor authentication</h2>
      <p className="text-sm text-[var(--text-dim)] mb-7">Enter the 6-digit code from your authenticator app.</p>
      {error && <Alert type="error" className="mb-5">{error}</Alert>}
      <form onSubmit={handle} className="space-y-5">
        <Input label="Authentication code" value={code} onChange={e => setCode(e.target.value)}
          placeholder="000000" maxLength={6} className="text-center text-xl font-mono tracking-widest" required />
        <Button type="submit" loading={loading} className="w-full justify-center py-3">Verify</Button>
      </form>
      <p className="text-center mt-4 text-sm text-[var(--text-dim)]">
        Lost your device? Use a <button className="text-[var(--color-accent)] hover:underline">backup code</button>
      </p>
    </>
  )
}
